/**
 * "Viewing as" — the company switcher.
 *
 * Purchasing is team / region / company managed. Most buyers hold one
 * company and see one book. A platform admin, or a buyer whose
 * `company_access` spans several brands, can narrow the worklist to ONE
 * company's divisions via `?company_id=<slug>`; this surfaces that as a
 * control.
 *
 * ## The server decides who may switch, not this file
 *
 * ⚠️ Pricing's equivalent re-derives the rule in the browser
 * (`user.role === 'admin' || company_access.length > 1`). This does not.
 * `/api/scope/companies` returns the exact list the caller may use and a
 * `can_switch` flag, and this renders what it is told.
 *
 * Two implementations of one permission rule is how they drift, and the
 * browser's copy is the one nobody tests. The server's answer is also the
 * one that actually gates the data, so a UI that disagreed would only ever
 * mislead — offering a company that 403s, or hiding one that would work.
 *
 * ## What an override actually changes
 *
 * Not a row filter. Purchasing gates DEMAND by division (`erp_stock` carries
 * no division, so supply stays whole — CLAUDE.md DO #11), and the override
 * simply resolves a different company's `buys_for` divisions. So "viewing as
 * Cabletec" means "the demand Cabletec is responsible for ordering", with
 * the coverage verdict still computed against the whole warehouse.
 *
 * ⚠️ A stale override must not lock anyone out. If the stored slug is no
 * longer in the caller's list — their access changed, or a mapping moved —
 * it is cleared on mount rather than left to 403 every request.
 */

import { api, store, getIcon, showModal } from '@isg/ui-kit';

const STORAGE_KEY = 'isg-purchasing.companyOverride';
const NAV_ID = 'company-switcher-nav';

let scopeCache = null;   // { items: [{slug,name}], can_switch: bool }


// ── Storage ───────────────────────────────────────────────────────────

export function getCompanyOverride() {
  try {
    return localStorage.getItem(STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

function setCompanyOverride(slug) {
  try {
    if (slug) localStorage.setItem(STORAGE_KEY, slug);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* localStorage unavailable — the override just won't survive a reload */
  }
}


// ── API client patch ──────────────────────────────────────────────────

/**
 * Append `?company_id=<slug>` to every request while an override is active.
 * Call once at boot, before any page fetches.
 *
 * ⚠️ `/scope/companies` is EXEMPT. Scoping the switcher's own list by the
 * override would let a bad value hide the means of correcting it.
 */
export function patchApiForCompanyOverride() {
  if (api.__companyOverridePatched) return;
  api.__companyOverridePatched = true;

  const original = api.request.bind(api);

  api.request = function patched(path, options = {}) {
    const override = getCompanyOverride();
    if (!override || typeof path !== 'string') return original(path, options);

    if (path.startsWith('/auth/') || path.startsWith('/api/auth/')
        || path.includes('/scope/companies')) {
      return original(path, options);
    }
    // An explicit company_id at the call site wins — the caller meant it.
    if (/[?&]company_id=/.test(path)) return original(path, options);

    const sep = path.includes('?') ? '&' : '?';
    return original(`${path}${sep}company_id=${encodeURIComponent(override)}`,
                    options);
  };
}


// ── Data ──────────────────────────────────────────────────────────────

async function _fetchScope() {
  if (scopeCache) return scopeCache;
  try {
    const d = await api.get('/scope/companies');
    scopeCache = { items: (d && d.items) || [], can_switch: !!(d && d.can_switch) };
  } catch {
    // Never break the app over a switcher. No list means no control.
    scopeCache = { items: [], can_switch: false };
  }
  return scopeCache;
}

function _nameFor(slug) {
  const hit = (scopeCache?.items || []).find((c) => c.slug === slug);
  return hit ? hit.name : slug;
}


// ── UI ────────────────────────────────────────────────────────────────

async function _render() {
  document.getElementById(NAV_ID)?.remove();

  const navHost = document.querySelector('.sidebar .sidebar-nav');
  if (!navHost) return;                       // login screen, no chrome yet

  const scope = await _fetchScope();
  const override = getCompanyOverride();

  // ⚠️ A stored slug the server no longer offers would 403 every request.
  // Clear it and carry on rather than leaving the user stuck behind an
  // error they cannot see the cause of.
  if (override && !scope.items.some((c) => c.slug === override)) {
    setCompanyOverride(null);
    window.location.reload();
    return;
  }
  if (!scope.can_switch) return;              // nothing to switch between

  const label = override ? _nameFor(override) : 'All my companies';

  const section = document.createElement('div');
  section.className = 'nav-section';
  section.id = NAV_ID;
  section.setAttribute('role', 'group');
  section.setAttribute('aria-label', 'Viewing as');

  // A non-default scope must be impossible to miss — every number on the
  // page is narrower than the user's actual access while it is set.
  const activeStyle = override
    ? 'background:var(--brand-800);border-left:2px solid var(--accent-500);'
    : '';
  const dot = override ? 'var(--accent-500)' : '#10b981';

  section.innerHTML = `
    <div class="nav-section-label" aria-hidden="true">Viewing as</div>
    <button type="button" class="nav-item" id="cs-trigger" style="${activeStyle}"
            aria-haspopup="dialog"
            title="${override
              ? `Showing only ${_esc(label)} — click to change`
              : 'Showing every company you cover — click to narrow'}">
      <span class="nav-item-icon" aria-hidden="true"
            style="display:inline-flex;align-items:center;justify-content:center">
        <span style="width:8px;height:8px;border-radius:50%;background:${dot};display:inline-block"></span>
      </span>
      <span class="nav-item-label" style="display:flex;flex-direction:column;line-height:1.25;min-width:0">
        ${override
          ? '<span style="font-size:0.625rem;text-transform:uppercase;letter-spacing:0.04em;color:var(--accent-500)">Viewing as</span>'
          : ''}
        <span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${_esc(label)}</span>
      </span>
      <span aria-hidden="true" style="margin-left:auto;opacity:0.6">${getIcon('chevron-down', 14)}</span>
    </button>
  `;

  navHost.insertBefore(section, navHost.firstChild);
  section.querySelector('#cs-trigger')
    .addEventListener('click', () => _openModal());
}

async function _openModal() {
  const scope = await _fetchScope();
  const current = getCompanyOverride();

  const content = document.createElement('div');
  content.innerHTML = `
    <style>.cs-row:hover{background:var(--gray-100,#f3f4f6) !important;}</style>
    <p style="margin:0 0 var(--space-3);font-size:var(--text-sm);color:var(--text-secondary);line-height:1.5">
      Narrows the worklist to the demand <strong>one company is responsible for
      ordering</strong>. Stock and inbound supply are not split — the coverage
      verdict stays whole.
    </p>
    <input id="cs-filter" class="form-input" type="search" placeholder="Filter companies…"
           style="margin-bottom:var(--space-3)" autocomplete="off" />
    <div id="cs-list" style="max-height:50vh;overflow-y:auto;display:flex;flex-direction:column;gap:2px"></div>
    <div style="border-top:1px solid var(--border);margin-top:var(--space-3);padding-top:var(--space-3)">
      <button id="cs-clear" type="button" class="btn btn-ghost btn-sm"
              style="width:100%;justify-content:flex-start" ${current ? '' : 'disabled'}>
        ${getIcon('refresh-cw', 14)} Show every company I cover
      </button>
    </div>
  `;

  showModal({ title: 'Viewing as', content, size: 'sm' });

  const listEl = content.querySelector('#cs-list');
  const filterEl = content.querySelector('#cs-filter');
  content.querySelector('#cs-clear').addEventListener('click', () => {
    setCompanyOverride(null);
    window.location.reload();
  });

  const draw = (q = '') => {
    const needle = q.trim().toLowerCase();
    const rows = scope.items.filter((c) => !needle
      || (c.name || '').toLowerCase().includes(needle)
      || (c.slug || '').toLowerCase().includes(needle));

    if (!rows.length) {
      listEl.innerHTML = `<div style="padding:var(--space-3);color:var(--text-tertiary)">${
        scope.items.length ? 'No matches.' : 'No companies available.'}</div>`;
      return;
    }
    listEl.innerHTML = rows.map((c) => `
      <button type="button" class="cs-row" data-slug="${_esc(c.slug)}"
              style="width:100%;text-align:left;padding:var(--space-2) var(--space-3);background:${
                c.slug === current ? 'var(--gray-100,#f3f4f6)' : 'transparent'
              };border:0;border-radius:var(--radius);cursor:pointer;display:flex;justify-content:space-between;align-items:center;gap:var(--space-2);color:var(--text-primary)">
        <span style="min-width:0">
          <strong style="display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${_esc(c.name)}</strong>
          <span style="color:var(--text-tertiary);font-size:var(--text-xs)">
            ${_esc(c.entity_label || c.entity_code || '')}${
              c.has_worklist === false
                ? ' · <span style="color:var(--amber-600,#b45309)">no worklist yet</span>'
                : ''}
          </span>
        </span>
        ${c.slug === current ? '<span style="color:var(--accent-500,#BD1B44);flex-shrink:0">●</span>' : ''}
      </button>`).join('');

    listEl.querySelectorAll('button[data-slug]').forEach((b) => {
      b.addEventListener('click', () => {
        setCompanyOverride(b.dataset.slug);
        window.location.reload();
      });
    });
  };

  draw();
  filterEl.addEventListener('input', (e) => draw(e.target.value));
  setTimeout(() => filterEl.focus(), 50);
}


// ── Public surface ────────────────────────────────────────────────────

/** Mount the control, and re-render whenever auth state lands. */
export function mountCompanySwitcher() {
  if (store.get('user')) _render();
  store.subscribe('user', () => _render());
}


function _esc(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
